/**
 * One search-history lookup per `search_id` per page load (OCT-4653).
 *
 * The results page and the chat preview recover a stored search through
 * `lookupStoredSearch`. Concurrent and repeated callers share one request, and
 * a miss (curated, brand-page and guest searches are never filed) is
 * remembered. A failed lookup resolves null and is not remembered, so the next
 * caller asks again.
 */

import { getSearchQueryById } from "@/lib/shop-agent-client";

import type { StoredSearch } from "openapi/shop_agent/client/types.gen";

const lookups = new Map<string, Promise<StoredSearch | null>>();

/** The stored search filed in history under `searchId`, or null when there is none. */
export function lookupStoredSearch(searchId: string): Promise<StoredSearch | null> {
  const cached = lookups.get(searchId);
  if (cached) return cached;

  const lookup: Promise<StoredSearch | null> = getSearchQueryById(searchId).catch(
    (error: unknown) => {
      console.debug("Failed to fetch search query:", searchId, error);
      if (lookups.get(searchId) === lookup) lookups.delete(searchId);
      return null;
    }
  );
  lookups.set(searchId, lookup);
  return lookup;
}

/**
 * Forgets a remembered miss for `searchId`, so the next lookup asks history
 * once more. The chat agent files its searches at tool time in the background,
 * so a lookup made before the search's `search` event can miss a search that is
 * filed by then. A found search stays remembered.
 */
export function forgetStoredSearchMiss(searchId: string): void {
  const lookup = lookups.get(searchId);
  if (!lookup) return;
  void lookup.then((stored) => {
    if (stored === null && lookups.get(searchId) === lookup) lookups.delete(searchId);
  });
}

/** Forgets every lookup. For tests. */
export function resetStoredSearchLookups(): void {
  lookups.clear();
}
